/**
 * Progress Sync Utility
 * 
 * Bridges localStorage progress to Supabase database for authenticated users.
 * Handles sync, conflict resolution, and error recovery.
 */

import { logger } from './logger';
import { supabase } from '../supabase/config';
import { storage, STORAGE_KEYS } from './storage';

class ProgressSyncManager {
  constructor() {
    this.syncInProgress = false;
    this.syncQueue = [];
    this.lastSyncTime = null;
    this.syncDebounceMs = 2000; // Debounce syncs to batch updates
    this.syncTimeout = null;
  }

  /**
   * Check if user is a demo/test user (not a real authenticated Supabase user)
   */
  isDemoUser(user) {
    if (!user || !user.id) return true;
    // Demo users have non-UUID IDs or is_demo flag
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.id);
    return !isUUID || user.is_demo || user.is_fallback || user.is_developer;
  }

  /**
   * Sync progress to database if user is authenticated
   * @param {Object} progress - Progress object to sync
   * @param {Object} user - Authenticated user object
   * @param {boolean} immediate - Whether to sync immediately (default: debounced)
   * @returns {Promise<{success: boolean, synced: boolean}>}
   */
  async syncProgress(progress, user, immediate = false) {
    // Skip if no user or guest user
    if (!user || user.is_guest) {
      return { success: true, synced: false, reason: 'no_user_or_guest' };
    }

    // Skip if demo/test user (not a real authenticated Supabase user)
    if (this.isDemoUser(user)) {
      logger.debug('Skipping sync for demo/test user');
      return { success: true, synced: false, reason: 'demo_user' };
    }

    // Skip if Supabase is disabled
    if (storage.get(STORAGE_KEYS.SUPABASE_DISABLED) === 'true') {
      logger.debug('Supabase disabled, skipping sync');
      return { success: true, synced: false, reason: 'supabase_disabled' };
    }

    // Debounce syncs unless immediate
    if (!immediate) {
      return this.debouncedSync(progress, user);
    }

    // Prevent concurrent syncs
    if (this.syncInProgress) {
      logger.debug('Sync already in progress, queuing');
      this.syncQueue.push({ progress, user });
      return { success: true, synced: false, reason: 'queued' };
    }

    return this.performSync(progress, user);
  }

  /**
   * Debounced sync - batches rapid updates
   */
  debouncedSync(progress, user) {
    return new Promise((resolve) => {
      // Clear existing timeout
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout);
      }

      // Store latest progress for sync
      this.pendingSync = { progress, user };

      // Set new timeout
      this.syncTimeout = setTimeout(async () => {
        if (this.pendingSync) {
          const result = await this.performSync(this.pendingSync.progress, this.pendingSync.user);
          resolve(result);
          this.pendingSync = null;
        }
      }, this.syncDebounceMs);
    });
  }

  /**
   * Perform the actual sync to database
   */
  async performSync(progress, user) {
    this.syncInProgress = true;
    
    try {
      // Transform progress to match database schema
      const dbProgress = this.transformProgressForDB(progress);

      // Update database
      const { error } = await supabase
        .from('users')
        .update({
          progress: dbProgress,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        // Check if it's an RLS error (including infinite recursion)
        if (error.code === '42501' || error.code === '42P17' || error.message.includes('RLS') || error.message.includes('policy') || error.message.includes('infinite recursion')) {
          logger.warn('RLS policy error - disabling Supabase sync:', error.message);
          storage.set(STORAGE_KEYS.SUPABASE_DISABLED, 'true');
          return { success: false, synced: false, reason: 'rls_error', error: error.message, code: error.code };
        }
        
        throw error;
      }

      this.lastSyncTime = Date.now();
      logger.debug('Progress synced to database successfully');
      
      return { success: true, synced: true };
    } catch (error) {
      logger.error('Progress sync failed:', error);
      
      // Queue for retry if network error
      if (error.message.includes('network') || error.message.includes('fetch')) {
        this.syncQueue.push({ progress, user });
        return { success: false, synced: false, reason: 'network_error', queued: true };
      }
      
      return { success: false, synced: false, reason: 'error', error: error.message };
    } finally {
      this.syncInProgress = false;
      
      // Process queue if any
      if (this.syncQueue.length > 0) {
        const next = this.syncQueue.shift();
        setTimeout(() => this.syncProgress(next.progress, next.user, true), 1000);
      }
    }
  }

  /**
   * Transform localStorage progress format to database format
   */
  transformProgressForDB(localProgress) {
    // Map localStorage progress structure to database structure
    return {
      score: localProgress.totalScore || 0,
      learning_streak: localProgress.streak || 0,
      completed_lessons: this.extractCompletedLessons(localProgress),
      achievements: localProgress.achievements || [],
      last_active: new Date().toISOString(),
      // Store additional data in a metadata field
      metadata: {
        completedSubjects: localProgress.completedSubjects || {},
        questionHistory: localProgress.questionHistory || {},
        stats: localProgress.stats || {},
        unlockedThemes: localProgress.unlockedThemes || [],
        selectedTheme: localProgress.selectedTheme || 'default',
        avatar: localProgress.avatar || 'default',
        certificates: localProgress.certificates || [],
        lastPlayed: localProgress.lastPlayed
      }
    };
  }

  /**
   * Extract completed lessons from progress structure
   */
  extractCompletedLessons(progress) {
    const lessons = [];
    const completedSubjects = progress.completedSubjects || {};
    
    Object.keys(completedSubjects).forEach(subject => {
      const subjectData = completedSubjects[subject];
      if (subjectData && subjectData.completed) {
        lessons.push({
          subject,
          score: subjectData.score || 0,
          completedAt: subjectData.completedAt || new Date().toISOString()
        });
      }
    });
    
    return lessons;
  }

  /**
   * Load progress from database and merge with localStorage
   * Handles conflict resolution
   */
  async loadProgressFromDB(user) {
    if (!user || user.is_guest) {
      return null;
    }

    // Skip if demo/test user
    if (this.isDemoUser(user)) {
      return null;
    }

    if (storage.get(STORAGE_KEYS.SUPABASE_DISABLED) === 'true') {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('progress')
        .eq('id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No profile yet, will be created on first save
          return null;
        }
        throw error;
      }

      if (!data || !data.progress) {
        return null;
      }

      // Transform database format back to localStorage format
      return this.transformProgressFromDB(data.progress);
    } catch (error) {
      logger.error('Failed to load progress from database:', error);
      
      // Disable sync if RLS error (including infinite recursion)
      if (error.code === '42501' || error.code === '42P17' || error.message.includes('RLS') || error.message.includes('infinite recursion')) {
        logger.warn('RLS infinite recursion detected - disabling Supabase sync. Run COMPREHENSIVE_RLS_FIX.sql to fix.');
        storage.set(STORAGE_KEYS.SUPABASE_DISABLED, 'true');
      }
      
      return null;
    }
  }

  /**
   * Transform database progress format back to localStorage format
   */
  transformProgressFromDB(dbProgress) {
    const metadata = dbProgress.metadata || {};
    
    return {
      totalScore: dbProgress.score || 0,
      streak: dbProgress.learning_streak || 0,
      achievements: dbProgress.achievements || [],
      completedSubjects: metadata.completedSubjects || {},
      questionHistory: metadata.questionHistory || {},
      stats: metadata.stats || {},
      unlockedThemes: metadata.unlockedThemes || ['default'],
      selectedTheme: metadata.selectedTheme || 'default',
      avatar: metadata.avatar || 'default',
      certificates: metadata.certificates || [],
      lastPlayed: metadata.lastPlayed || null
    };
  }

  /**
   * Resolve conflicts between localStorage and database
   * Strategy: Use most recent data, prefer higher scores
   */
  resolveConflict(localProgress, dbProgress) {
    const localTime = localProgress.lastPlayed ? new Date(localProgress.lastPlayed).getTime() : 0;
    const dbTime = dbProgress.metadata?.lastPlayed ? new Date(dbProgress.metadata.lastPlayed).getTime() : 0;
    
    // Use most recent, but prefer higher scores if close in time
    const timeDiff = Math.abs(localTime - dbTime);
    const useLocal = localTime > dbTime || (timeDiff < 60000 && localProgress.totalScore >= (dbProgress.score || 0));
    
    if (useLocal) {
      logger.debug('Using localStorage progress (more recent or higher score)');
      return localProgress;
    } else {
      logger.debug('Using database progress (more recent)');
      return this.transformProgressFromDB(dbProgress);
    }
  }

  /**
   * Force immediate sync (used for critical updates)
   */
  async forceSync(progress, user) {
    return this.syncProgress(progress, user, true);
  }
}

// Export singleton instance
export const progressSync = new ProgressSyncManager();

export default progressSync;

